import { ROUTES } from '#/config/routes';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncSingleFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { resolveMultiFilterLabels } from '#/modules/commonComponents/table/columnFilterUtils';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { searchCustomerFilterOptions } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import QuotationStatusBadge from '#/modules/crmMgmt/quotation/components/QuotationStatusBadge';
import {
  formatQuotationCustomer,
  formatQuotationRoundingMode,
  formatQuotationTotal,
} from '#/modules/crmMgmt/quotation/helper/quotationFormat';
import { QUOTATION_STATUS_FILTER_OPTIONS } from '#/modules/crmMgmt/quotation/helper/quotationStatus';
import { formatDateOrDash, formatDateTimeOrDash } from '#/utils/date';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
}

/** 後端 status 只收單值，篩選用固定單選。列表固定依單號降冪、不收 sort，所有欄位關閉排序。 */
function createFixedSingleFilterMeta({ key, label, options, filterValue, onChange }) {
  return {
    filterVariant: 'selectSingle',
    filterMode: 'server',
    facet: 'fixed',
    multiValue: false,
    options,
    filterStatusKey: key,
    filterStatusLabel: label,
    filterValue,
    onServerFilterChange: onChange,
    resolveFilterDisplay: (values) => resolveMultiFilterLabels(values, options),
  };
}

export function buildQuotationColumns({
  filters = {},
  onFilterChange,
  customerLabelCache,
  canShowEdit = (row) => row.status === 'DRAFT',
} = {}) {
  const columns = [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.quotationDetail(row.id),
      getEditPath: (row) => ROUTES.quotationEdit(row.id),
      canShowEdit,
      size: 80,
      minSize: 80,
    }),
    {
      id: 'quotationNo',
      header: '報價單號',
      accessorKey: 'quotationNo',
      accessorFn: (row) => displayDash(row.quotationNo),
      size: 130,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'quotationNo',
        label: '報價單號',
        filterValue: filters.quotationNo,
        onChange: (value) => onFilterChange('quotationNo', value),
        placeholder: '搜尋報價單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        row.original.status
          ? <QuotationStatusBadge status={row.original.status} size="sm" />
          : '-'
      ),
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedSingleFilterMeta({
          key: 'status',
          label: '狀態',
          options: QUOTATION_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'customer',
      header: '客戶',
      accessorFn: (row) => formatQuotationCustomer(row.customer),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncSingleFilterMeta({
          key: 'customerId',
          label: '客戶',
          searchOptions: searchCustomerFilterOptions,
          filterValue: filters.customerId,
          onChange: (value) => onFilterChange('customerId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(customerLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(customerLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'validUntil',
      header: '有效截止日',
      accessorFn: (row) => formatDateOrDash(row.validUntil),
      size: 120,
    },
    {
      id: 'contactName',
      header: '聯絡人',
      accessorFn: (row) => displayDash(row.contactName),
      size: 120,
    },
    {
      id: 'totalAmount',
      header: '總額（含稅）',
      accessorFn: (row) => formatQuotationTotal(row.amountSummary?.totalAmount),
      size: 120,
      enableSorting: false,
      meta: { cellClassName: 'td_right' },
    },
    {
      id: 'roundingMode',
      header: '總額取整',
      accessorFn: (row) => formatQuotationRoundingMode(row.roundingMode),
      size: 100,
      enableSorting: false,
    },
    {
      id: 'lineCount',
      header: '主項目數',
      accessorFn: (row) => (row.lines ?? []).length,
      size: 80,
      enableSorting: false,
    },
    {
      id: 'updatedAt',
      header: '更新時間',
      accessorFn: (row) => formatDateTimeOrDash(row.record?.updatedAt),
      size: 160,
    },
    {
      id: 'remark',
      header: '內部備註',
      accessorKey: 'remark',
      accessorFn: (row) => displayDash(row.remark),
      size: 180,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />
      ),
    },
  ];
  return columns.map((column) => ({ ...column, enableSorting: false }));
}
